import { clients } from 'lib/https';
import {
  IAgentRetrievalTestingPayload,
  IAgentRetrievalTestingResponse,
  IGetAgentByIdPayload,
  IGetAgentByIdResponse,
  IGetAgentsPayload,
  IGetAgentsResponse,
  IProcessFilesPayload,
  IAgentChatStreamPayload,
  IChangeAgentStatusPayload,
  IChangeAgentStatusResponse,
  ICreateAgentPayload,
  ICreateAgentResponse,
  IDeleteAgentPayload,
  IDeleteAgentResponse,
  ILMTQueryAgentPayload,
  ILMTQueryAgentResponse,
  IPublishAgentPayload,
  IPublishAgentResponse,
  IQueryAgentPayload,
  IQueryAgentResponse,
  ISSEQueryAgentPayload,
  IUpdateAgentAIConfigurationPayload,
  IUpdateAgentAIConfigurationResponse,
  IUpdateAgentPersonaPayload,
  IUpdateAgentPersonaResponse,
  IUpdateAgentWidgetPayload,
  IUpdateAgentWidgetResponse,
  GuardrailCatalogItem,
  IWidgetColorSuggestionPayload,
  IWidgetColorSuggestionResponse,
  IUpdateAgentPreferencesPayload,
} from '../types/agent.service.type';
import type { SaveShareRequest, ShareStateResponse } from '../types/agent-sharing.type';
import { getConfigurationSettings } from 'services/configuration-service';

export class AgentService {
  async getAgents(payload: IGetAgentsPayload, blocksKey: string): Promise<IGetAgentsResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsQueries}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async getAgentById(payload: IGetAgentByIdPayload, blocksKey: string): Promise<IGetAgentByIdResponse> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsQuery}/${payload.id}?project_key=${payload.projectKey}`,
      blocksKey
    );
  }

  async createAgent(payload: ICreateAgentPayload, blocksKey: string): Promise<ICreateAgentResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsCreate}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async deleteAgent(payload: IDeleteAgentPayload, blocksKey: string): Promise<IDeleteAgentResponse> {
    const config = await getConfigurationSettings();
    const url =
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsDelete}/${payload.id}` +
      `?project_key=${encodeURIComponent(payload.project_key)}`;

    return clients.delete(url, blocksKey);
  }

  async changeAgentStatus(
    payload: IChangeAgentStatusPayload,
    blocksKey: string
  ): Promise<IChangeAgentStatusResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsChangeStatus}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async updateAgentPersona(
    payload: IUpdateAgentPersonaPayload,
    blocksKey: string
  ): Promise<IUpdateAgentPersonaResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsUpdatePersona}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async updateAgentAIConfiguration(
    payload: IUpdateAgentAIConfigurationPayload,
    blocksKey: string
  ): Promise<IUpdateAgentAIConfigurationResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsUpdateAiConfig}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async updateAgentWidget(
    payload: IUpdateAgentWidgetPayload,
    blocksKey: string
  ): Promise<IUpdateAgentWidgetResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsUpdateWidget}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async query(
    payload: IQueryAgentPayload,
    blocksKey: string
  ): Promise<IQueryAgentResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentQuery}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async lmtQuery(
    payload: ILMTQueryAgentPayload,
    blocksKey: string
  ): Promise<ILMTQueryAgentResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentQueryLmt}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async lmtQueryStream(
    payload: ILMTQueryAgentPayload,
    blocksKey: string
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    const config = await getConfigurationSettings();
    return clients.stream(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentQueryLmtStream}`,
      blocksKey,
      JSON.stringify(payload),
      { Accept: 'text/event-stream' }
    );
  }

  async queryStream(
    payload: ISSEQueryAgentPayload,
    blocksKey: string
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    const config = await getConfigurationSettings();
    return clients.stream(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentQueryStream}`,
      blocksKey,
      JSON.stringify(payload),
      { Accept: 'text/event-stream' }
    );
  }

  async widgetColorSuggestion(
    payload: IWidgetColorSuggestionPayload,
    blocksKey: string
  ): Promise<IWidgetColorSuggestionResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsWidgetColorSuggestion}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async retrievalTesting(
    agentId: string,
    payload: IAgentRetrievalTestingPayload,
    blocksKey: string
  ): Promise<IAgentRetrievalTestingResponse> {
    const config = await getConfigurationSettings();
    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbRetrievalTest}/${agentId}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async publishAgents(
    payload: IPublishAgentPayload,
    blocksKey: string
  ): Promise<IPublishAgentResponse> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsPublish}/${payload.agent_id}?project_key=${encodeURIComponent(payload.project_key)}`,
      blocksKey
    );
  }

  async agentChatStream(widgetId: string, body: IAgentChatStreamPayload, blocksKey: string, sessionId?: string) {
    const config = await getConfigurationSettings();
    const queryParams = sessionId ? `?s_id=${sessionId}` : '';
    const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentChat}/${widgetId}${queryParams}`;
    return clients.stream(url, blocksKey, JSON.stringify(body));
  }

  async processFiles(payload: IProcessFilesPayload, blocksKey: string) {
    const config = await getConfigurationSettings();
    return clients.stream(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.aiAgentFileProcess}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  private shareUrl(config: Awaited<ReturnType<typeof getConfigurationSettings>>, agentId: string) {
    return `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsShare}/${encodeURIComponent(agentId)}/share`;
  }

  /** Owner-only: load visibility + participants + orgGrants for Share modal. */
  async getShare(agentId: string, blocksKey: string): Promise<ShareStateResponse> {
    const config = await getConfigurationSettings();
    return clients.get(this.shareUrl(config, agentId), blocksKey);
  }

  /** Owner-only: replace full share state (Share modal save). camelCase body. */
  async saveShare(
    agentId: string,
    payload: SaveShareRequest,
    blocksKey: string
  ): Promise<ShareStateResponse> {
    const config = await getConfigurationSettings();
    return clients.post(this.shareUrl(config, agentId), blocksKey, JSON.stringify(payload));
  }

  async updateAgentPreferences(
    payload: IUpdateAgentPreferencesPayload,
    blocksKey: string
  ): Promise<void> {
    const config = await getConfigurationSettings();
    const url =
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsShare}` +
      `/${encodeURIComponent(payload.agentId)}/preferences`;

    await clients.put(url, blocksKey, JSON.stringify({ is_hidden: payload.is_hidden }));
  }

  async getGuardrailsCatalog(blocksKey: string): Promise<GuardrailCatalogItem[]> {
    const config = await getConfigurationSettings();
    return clients.get(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.agentsGuardrails}`,
      blocksKey
    );
  }
}

export const agentService = new AgentService();
